import type { WordsType } from './i18n/words.js';

/**
 * PX-19 (`docs/concept-design-agency-production-and-automation.md` §24.5, D-0428 c): who a customer
 * reads as the sender of every answer and receipt the team sends them — PX-2's reply sentence, PX-7's
 * change sentence, the team's reply to a question, and PX-20's AI line. It is a per-work-area setting,
 * `policies.customerSender`:
 *
 * - `person`, the default: "Lea at Pixidust", the member who sent it, with the agency's name;
 * - `role`: "Support at Pixidust", the display name of the one role `roleKey` names, for a desk where
 *   answers come from a role rather than a person;
 * - `agency`: "Pixidust", never a person.
 *
 * The role's display name is the one role name PX-1 lets reach a customer, and only here: a role
 * whose name cannot be read is never replaced by its key, the reader falls back to the agency.
 *
 * It replaces the New item block's own `namePerson` choice: a work area that stored none of this
 * section but switched `namePerson` off reads as `agency`, so the reply sentence and the change line
 * cannot disagree. The web and the server both read the setting and build the words here.
 */
export const CUSTOMER_SENDER_POLICY_KEY = 'customerSender';

export const CUSTOMER_SENDER_MODES = ['person', 'role', 'agency'] as const;

export type CustomerSenderModeType = (typeof CUSTOMER_SENDER_MODES)[number];

export interface ICustomerSender {
  mode: CustomerSenderModeType;
  /** The key of the role whose display name answers; only, and always, under `role`. */
  roleKey?: string;
  /**
   * PX-31 (I-2384): under `person`, the member who answers new requests, so the reply after sending
   * names them to every customer ("Jonas will get back to you"). Absent, the reply names the member
   * who invited the reader, while they still answer here, else the agency; never a guess.
   */
  userId?: string;
  /** PX-31: under `person`, the reply after sending names nobody but the agency (no `userId`). */
  namesNobody?: true;
}

/**
 * I-2363 (PX-19b): the code of the server's refusal of a role key the work area does not offer to a
 * person, so a customer's answers would name nobody. The web words it from its catalogue
 * (`settings.customerSender.roleUnknown`); the CLI prints the message.
 */
export const CUSTOMER_SENDER_ROLE_UNKNOWN = 'customer_sender_role_unknown';

/** The server's sentence for {@link CUSTOMER_SENDER_ROLE_UNKNOWN}: the key, and the keys it may name. */
export function customerSenderRoleUnknownMessage(
  field: string,
  roleKey: string,
  known: readonly string[],
): string {
  return (
    `${field} names the role ${roleKey}, which this work area does not have, so nobody would be ` +
    `named. Name one of ${known.join(', ')}. Nothing was written.`
  );
}

/** The longest name an invitation says an agency by. */
export const AGENCY_NAME_MAX_LENGTH = 80;

/**
 * A name an agency is said by, safe to show and to send in: no control characters or line breaks,
 * none of `"`, `\\`, `<`, `>` or `@` (it never reads as an address: "PayPal service@paypal.com"),
 * whitespace collapsed, at most {@link AGENCY_NAME_MAX_LENGTH} characters; `null` when nothing is
 * left. Every source of an agency's name goes through it — the block, the organisation, the inviter.
 */
export function agencyDisplayName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value
    // eslint-disable-next-line no-control-regex -- the characters a header must never carry
    .replace(/[\u0000-\u001F\u007F-\u009F\u2028\u2029]/g, ' ')
    // Re-review: invisible and direction marks go, and the look-alike at signs are at signs.
    .replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '')
    .replace(/["\\<>@\uFF20\uFE6B]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, AGENCY_NAME_MAX_LENGTH)
    .trim();
  return trimmed || null;
}

/**
 * PX-33 (I-2384, point 3), for PX-19 and PX-62: the agency's name as a customer reads it — the one
 * the reply promise names (`config.replyPromise.agency`), else the display name of the organisation
 * the work area is in, else `null`. Never the work area's own name, which the agency chose for itself
 * ("Agency walkthrough 1793440154535"): a work area in a person's own namespace passes no
 * organisation, and the caller then names nobody. Both go through {@link agencyDisplayName}.
 */
export function customerAgencyNameOf(input: {
  promised?: unknown;
  organisation?: string | null;
}): string | null {
  return agencyDisplayName(input.promised) ?? agencyDisplayName(input.organisation);
}

/** The setting with nothing stored. */
export const DEFAULT_CUSTOMER_SENDER: ICustomerSender = { mode: 'person' };

/** A role key as roles are keyed: lower-case words joined by `-`, `_` or `.`. */
const ROLE_KEY_PATTERN = /^[a-z0-9][a-z0-9._-]{0,99}$/;
const USER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCustomerSenderMode(value: unknown): value is CustomerSenderModeType {
  return (CUSTOMER_SENDER_MODES as readonly unknown[]).includes(value);
}

/**
 * What is wrong with the `customerSender` section of a `policies` write, or null: the words that
 * follow `policies.customerSender` in the server's refusal. A role key is required for `role` and
 * refused otherwise, and nothing else is kept, so the section is the whole setting or nothing.
 */
export function customerSenderPolicyProblem(section: unknown): string | null {
  if (section === undefined || section === null) return null;
  if (typeof section !== 'object' || Array.isArray(section)) {
    return `must be an object naming mode as ${CUSTOMER_SENDER_MODES.join(', ')} — ${JSON.stringify(section)} is not.`;
  }
  const value = section as Record<string, unknown>;
  const extra = Object.keys(value).filter(
    (key) => key !== 'mode' && key !== 'roleKey' && key !== 'userId' && key !== 'namesNobody',
  );
  if (extra.length > 0) {
    return `holds only mode, roleKey, userId and namesNobody — not ${extra.join(', ')}.`;
  }
  // PX-31: naming nobody is a choice under person, and not beside a member who answers.
  if (value.namesNobody !== undefined && value.namesNobody !== null) {
    if (value.namesNobody !== true) {
      return `must say namesNobody as true — ${JSON.stringify(value.namesNobody)} is not.`;
    }
    if (value.mode !== 'person') {
      return `names nobody (namesNobody) only under person — not under ${String(value.mode)}.`;
    }
    if (value.userId !== undefined && value.userId !== null) {
      return 'names a member (userId) or nobody (namesNobody), not both.';
    }
  }
  // PX-31: the member who answers new requests, a user id, and only under person.
  if (value.userId !== undefined && value.userId !== null) {
    if (value.mode !== 'person')
      return `names a userId only under person — not under ${String(value.mode)}.`;
    if (typeof value.userId !== 'string' || !USER_ID_PATTERN.test(value.userId)) {
      return `must name the member who answers new requests as userId — ${JSON.stringify(value.userId)} is not a user id.`;
    }
  }
  if (!isCustomerSenderMode(value.mode)) {
    return `must name mode as ${CUSTOMER_SENDER_MODES.join(', ')} — ${JSON.stringify(value.mode)} is not.`;
  }
  if (value.mode === 'role') {
    if (typeof value.roleKey !== 'string' || !ROLE_KEY_PATTERN.test(value.roleKey)) {
      return `must name the role whose name answers as roleKey under role — ${JSON.stringify(value.roleKey)} is not a role key.`;
    }
    return null;
  }
  if (value.roleKey !== undefined) {
    return `names a roleKey only under role — not under ${value.mode}.`;
  }
  return null;
}

/**
 * The setting a work area's `policies` hold: the stored value if it is one, else `agency` where the
 * New item block's `namePerson` was switched off (`legacyNamePerson: false`), else `person`. A
 * malformed stored value reads as if nothing were stored; a write of one is refused.
 */
export function customerSenderOf(policies: unknown, legacyNamePerson = true): ICustomerSender {
  const section =
    policies && typeof policies === 'object' && !Array.isArray(policies)
      ? (policies as Record<string, unknown>)[CUSTOMER_SENDER_POLICY_KEY]
      : undefined;
  if (section !== undefined && section !== null && customerSenderPolicyProblem(section) === null) {
    const value = section as {
      mode: CustomerSenderModeType;
      roleKey?: string;
      userId?: string | null;
      namesNobody?: true | null;
    };
    if (value.mode === 'role') return { mode: 'role', roleKey: value.roleKey };
    if (value.mode === 'person' && value.userId) return { mode: 'person', userId: value.userId };
    if (value.mode === 'person' && value.namesNobody) return { mode: 'person', namesNobody: true };
    return { mode: value.mode };
  }
  return legacyNamePerson ? { ...DEFAULT_CUSTOMER_SENDER } : { mode: 'agency' };
}

/**
 * The merge patch that stores the setting: this section whole, with `roleKey` removed outside `role`,
 * so a switch from a role to the agency leaves no role behind.
 */
export function customerSenderPolicyPatch(sender: ICustomerSender): Record<string, unknown> {
  return {
    [CUSTOMER_SENDER_POLICY_KEY]: {
      mode: sender.mode,
      roleKey: sender.mode === 'role' ? (sender.roleKey ?? null) : null,
      userId: sender.mode === 'person' ? (sender.userId ?? null) : null,
      namesNobody: sender.mode === 'person' && !sender.userId && sender.namesNobody ? true : null,
    },
  };
}

/**
 * The setting as a reader is told it with a work area (`IProject.customerSender`): the mode the server
 * reads — the stored one, else `agency` where a New item block switched `namePerson` off, else
 * `person` — and under `role` the role's display name, never its key (PX-1).
 */
export interface ICustomerSenderView {
  mode: CustomerSenderModeType;
  roleName?: string | null;
  /**
   * PX-31 (I-2384): the work area stores the setting (rather than reading the default or a page's
   * legacy `namePerson`), so a block's own static person no longer speaks for it.
   */
  stored?: boolean;
  /**
   * PX-31: under `person`, the display name of who the reply after sending names for this reader:
   * the member the setting names, else the member who invited the reader (while either still answers
   * here); none, the agency.
   */
  personName?: string | null;
}

/**
 * The setting a reader acts on: the work area's view if the server sent one, else what the policies
 * say, else the legacy `namePerson` (`customerSenderOf`).
 */
export function customerSenderFromView(
  view: ICustomerSenderView | null | undefined,
  legacyNamePerson = true,
): ICustomerSender {
  if (view && isCustomerSenderMode(view.mode)) return { mode: view.mode };
  return customerSenderOf(undefined, legacyNamePerson);
}

/**
 * The setting a work area's settings screen shows: the one the server reads (`customerSenderOf`).
 * The stored value from `policies` (which keeps the role's key), else the server's reading
 * (`customerSender`), which alone knows the pages' `namePerson`: nothing stored and that reading
 * `agency` is the legacy `namePerson: false`.
 */
export function customerSenderOfWorkArea(workArea: {
  policies?: unknown;
  customerSender?: ICustomerSenderView | null;
}): ICustomerSender {
  return customerSenderOf(workArea.policies, workArea.customerSender?.mode !== 'agency');
}

/** What a sender is said from: who sent it, the setting's role's display name, the agency's name. */
export interface ICustomerSenderFacts {
  /** The member who sent it, by name; none for a message no person sent (an agent's, the system's). */
  person?: string | null;
  /** The display name of the role the setting names; none when it cannot be read. */
  roleName?: string | null;
  /** The agency's name: the package's, else the organisation's; none in a person's own namespace. */
  agency?: string | null;
}

/** Who answers, in parts: the name before "at" (a person's or a role's), and the agency. */
export interface ICustomerSenderParts {
  name: string | null;
  agency: string | null;
}

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

/**
 * Who answers under `sender`, in parts. `person` names the member who sent it; `role` names the role's
 * display name; `agency` names no one but the agency. A person or role that cannot be named leaves
 * the agency alone, never a key or an agent's name.
 */
export function customerSenderParts(
  sender: ICustomerSender,
  facts: ICustomerSenderFacts,
): ICustomerSenderParts {
  const agency = trimmed(facts.agency);
  const name =
    sender.mode === 'person'
      ? trimmed(facts.person)
      : sender.mode === 'role'
        ? trimmed(facts.roleName)
        : null;
  return { name, agency };
}

/**
 * The sender as a customer reads them, in the reader's words: "Lea at Pixidust", "Support at
 * Pixidust", "Pixidust", "Lea" where there is no agency to name, or null where there is nobody to name
 * (the reader then says "the team").
 */
export function customerSenderLine(
  words: WordsType,
  sender: ICustomerSender,
  facts: ICustomerSenderFacts,
): string | null {
  const { name, agency } = customerSenderParts(sender, facts);
  if (name && agency) return words('notifications.customerSender.nameAtAgency', { name, agency });
  return name ?? agency;
}
